import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { RootState } from '../store/store';
import { showCustomToast } from '../components/common/CustomToast';
import { getValidToken } from '../utils/auth';
import { updateUser } from '../store/slices/authSlice';
import { priceService } from '../services/priceService';
import LoadingSpinner from '../components/common/LoadingSpinner';
import SettingsSkeleton from '../components/common/SettingsSkeleton';
import SEO from '../components/common/SEO';
import phantomImg from '../assets/phantom.png';
import './SettingsPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface ProfileStats {
  user: {
    id: string;
    username: string;
    email: string;
    balance: string;
    totalWagered: string;
    totalWon: string;
    totalLost: string;
    netProfit: string;
    createdAt: string;
    profilePicture: string | null;
  };
  stats: {
    totalBets: number;
    winRate: number;
    avgBetSize: number;
    recentBetsCount: number;
  };
}

interface Transaction {
  id: string;
  type: 'deposit' | 'withdrawal' | 'bet' | 'payout' | 'voucher_redemption';
  amount: string;
  status: 'pending' | 'confirmed' | 'failed';
  createdAt: string;
  metadata?: any;
}

type ActiveView = 'profile' | 'transactions' | 'affiliates';

const SettingsPage: React.FC = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { user, token } = useSelector((state: RootState) => state.auth);
  
  const [profileStats, setProfileStats] = useState<ProfileStats | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  
  // Check for tab parameter in URL
  const tabParam = searchParams.get('tab') as ActiveView | null;
  const [activeView, setActiveView] = useState<ActiveView>(tabParam || 'profile');
  
  const [solPrice, setSolPrice] = useState(186);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const transactionsPerPage = 4;
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Affiliate state
  const [affiliateInfo, setAffiliateInfo] = useState<any>(null);
  const [affiliateStats, setAffiliateStats] = useState<any>(null);
  const [affiliateCode, setAffiliateCode] = useState('');
  const [claimCode, setClaimCode] = useState('');
  const [affiliatePage, setAffiliatePage] = useState(1);
  const [isSavingCode, setIsSavingCode] = useState(false);
  const [isClaimingCode, setIsClaimingCode] = useState(false);
  const [isClaimingEarnings, setIsClaimingEarnings] = useState(false);

  // Check URL params for tab
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'transactions') {
      setActiveView('transactions');
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchData = async () => {
      if (!token || !user) {
        setLoading(false);
        return; // Don't fetch data if not logged in
      }
      
      const startTime = Date.now();
      
      try {
        // Fetch profile stats
        const profileResponse = await fetch(`${API_BASE_URL}/api/profile/stats`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (profileResponse.ok) {
          const contentType = profileResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const data = await profileResponse.json();
            setProfileStats(data);
          }
        }

        // Fetch transactions with pagination
        const txResponse = await fetch(`${API_BASE_URL}/api/users/transactions?page=${currentPage}&limit=${transactionsPerPage}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
        
        if (txResponse.ok) {
          const contentType = txResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const txData = await txResponse.json();
            setTransactions(txData.transactions || []);
            setTotalPages(txData.pagination?.pages || 1);
          }
        }

        // Fetch SOL price
        const price = await priceService.getSOLPrice();
        setSolPrice(price);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        // Ensure skeleton shows for at least 2 seconds
        const elapsed = Date.now() - startTime;
        const remainingTime = Math.max(0, 2000 - elapsed);
        
        setTimeout(() => {
          setLoading(false);
        }, remainingTime);
      }
    };

    fetchData();
  }, [token, user, currentPage]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showCustomToast.error('Please select a valid image file (JPEG, PNG, or WebP)');
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      showCustomToast.error('File size must be less than 10MB');
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('profilePicture', file);

      const authToken = getValidToken();
      if (!authToken) {
        showCustomToast.error('Please log in to upload a profile picture');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/profile/upload-picture`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        const filename = data.profilePicture;
        dispatch(updateUser({ profilePicture: filename }));
        setProfileStats(prev => prev ? {...prev, user: {...prev.user, profilePicture: filename}} : null);
        showCustomToast.success('Profile picture uploaded successfully!');
      } else {
        showCustomToast.error(data.error || 'Failed to upload profile picture');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showCustomToast.error('Failed to upload profile picture');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemovePicture = async () => {
    const currentPicture = profileStats?.user.profilePicture || user?.profilePicture;
    if (!currentPicture) {
      showCustomToast.error('No profile picture to remove');
      return;
    }

    setIsRemoving(true);

    try {
      const authToken = getValidToken();
      if (!authToken) {
        showCustomToast.error('Please log in to remove your profile picture');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/profile/remove-picture`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        dispatch(updateUser({ profilePicture: null }));
        setProfileStats(prev => prev ? {...prev, user: {...prev.user, profilePicture: null}} : null);
        showCustomToast.success('Profile picture removed successfully!');
      } else {
        showCustomToast.error(data.error || 'Failed to remove profile picture');
      }
    } catch (error) {
      console.error('Remove error:', error);
      showCustomToast.error('Failed to remove profile picture');
    } finally {
      setIsRemoving(false);
    }
  };

  // Affiliate functions - Memoized to prevent infinite loops
  const fetchAffiliateInfo = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/affiliate/info`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAffiliateInfo(data);
        setAffiliateCode(data.affiliateCode || '');
      }
    } catch (error) {
      console.error('Error fetching affiliate info:', error);
    }
  }, [token]);

  const fetchAffiliateStats = useCallback(async (page: number) => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/affiliate/stats?page=${page}&limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAffiliateStats(data);
      }
    } catch (error) {
      console.error('Error fetching affiliate stats:', error);
    }
  }, [token]);
  
  // Load affiliate data when view changes
  useEffect(() => {
    if (activeView === 'affiliates' && token) {
      fetchAffiliateInfo();
      fetchAffiliateStats(affiliatePage);
    }
  }, [activeView, token, affiliatePage, fetchAffiliateInfo, fetchAffiliateStats]);

  const handleSaveAffiliateCode = async () => {
    const trimmedCode = affiliateCode.trim();
    
    if (!trimmedCode) {
      showCustomToast.error('Please enter an affiliate code');
      return;
    }

    // Validate letters only
    const lettersOnlyRegex = /^[a-zA-Z]{3,20}$/;
    if (!lettersOnlyRegex.test(trimmedCode)) {
      showCustomToast.error('Affiliate code must be 3-20 letters only (no numbers or special characters)');
      return;
    }

    setIsSavingCode(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/affiliate/set-code`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: trimmedCode })
      });

      const data = await response.json();
      if (response.ok) {
        showCustomToast.success('Affiliate code saved successfully!');
        fetchAffiliateInfo();
      } else {
        showCustomToast.error(data.error || 'Failed to save affiliate code');
      }
    } catch (error) {
      showCustomToast.error('Failed to save affiliate code');
    } finally {
      setIsSavingCode(false);
    }
  };

  const handleClaimCode = async () => {
    if (!claimCode.trim()) {
      showCustomToast.error('Please enter an affiliate code to claim');
      return;
    }

    setIsClaimingCode(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/affiliate/claim`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: claimCode })
      });

      const data = await response.json();
      if (response.ok) {
        showCustomToast.success(data.message);
        setClaimCode('');
        fetchAffiliateInfo();
      } else {
        showCustomToast.error(data.error || 'Failed to claim affiliate code');
      }
    } catch (error) {
      showCustomToast.error('Failed to claim affiliate code');
    } finally {
      setIsClaimingCode(false);
    }
  };

  const handleClaimEarnings = async () => {
    const earningsAmount = Number(affiliateStats?.stats?.totalEarnings || 0) / 1e9;
    const MIN_CLAIM_SOL = 0.067;

    if (earningsAmount < MIN_CLAIM_SOL) {
      showCustomToast.error(`Minimum claim amount is ${MIN_CLAIM_SOL} SOL. You currently have ${earningsAmount.toFixed(4)} SOL.`);
      return;
    }

    setIsClaimingEarnings(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/affiliate/claim-earnings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok) {
        showCustomToast.success(data.message);
        // Refresh affiliate stats to show 0 earnings
        fetchAffiliateStats(affiliatePage);
      } else {
        showCustomToast.error(data.error || 'Failed to claim earnings');
      }
    } catch (error) {
      showCustomToast.error('Failed to claim earnings');
    } finally {
      setIsClaimingEarnings(false);
    }
  };

  const copyTransactionData = (tx: Transaction) => {
    // Create game verification data for coinflip games
    if (tx.metadata?.gameType === 'coinflip' && tx.metadata?.serverSeed && tx.metadata?.clientSeed && tx.metadata?.gameId) {
      const gameData = {
        gameId: tx.metadata.gameId,
        serverSeed: tx.metadata.serverSeed,
        clientSeed: tx.metadata.clientSeed
      };
      
      // Save to localStorage for pasting in provably fair page
      localStorage.setItem('gameVerificationData', JSON.stringify(gameData));
      
      // Also copy to clipboard for manual use
      const copyText = `Game ID: ${gameData.gameId}\nServer Seed: ${gameData.serverSeed}\nClient Seed: ${gameData.clientSeed}`;
      navigator.clipboard.writeText(copyText);
      
      showCustomToast.success('Game verification data copied! You can now paste it on the Provably Fair page.');
    } else {
      // For non-game transactions, copy basic transaction info
      const transactionText = `Transaction ID: ${tx.id}\nType: ${tx.type}\nAmount: ${(Number(tx.amount) / 1_000_000_000).toFixed(4)} SOL\nStatus: ${tx.status}\nDate: ${new Date(tx.createdAt).toLocaleString()}`;
      navigator.clipboard.writeText(transactionText);
      showCustomToast.success('Transaction details copied to clipboard!');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showCustomToast.success('Copied to clipboard!');
  };

  if (!user || loading) {
    return (
      <>
        <SEO 
          title="Settings - SolBlox"
          description="Manage your SolBlox account settings, view transaction history, and track your gaming statistics."
          url="https://solblox.com/settings"
        />
        <SettingsSkeleton />
      </>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getImageSrc = () => {
    const currentPicture = profileStats?.user.profilePicture || user.profilePicture;
    if (currentPicture) {
      if (currentPicture.startsWith('http')) {
        return currentPicture;
      }
      return `${API_BASE_URL}/api/profile/picture/${currentPicture}`;
    }
    return phantomImg;
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'deposit':
      case 'voucher_redemption':
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
          </svg>
        );
      case 'withdrawal':
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 19V5M5 12l7-7 7 7"/>
          </svg>
        );
      case 'bet':
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <circle cx="15.5" cy="15.5" r="1.5"/>
            <circle cx="8.5" cy="15.5" r="1.5"/>
            <circle cx="15.5" cy="8.5" r="1.5"/>
          </svg>
        );
      case 'payout':
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="8" r="7"/>
            <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/>
          </svg>
        );
      default:
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="20" x2="12" y2="10"/>
            <line x1="18" y1="20" x2="18" y2="4"/>
            <line x1="6" y1="20" x2="6" y2="16"/>
          </svg>
        );
    }
  };

  const getStatusBadge = (status: string) => {
    const statusClass = status === 'confirmed' ? 'status-confirmed' : status === 'pending' ? 'status-pending' : 'status-failed';
    return <span className={`status-badge ${statusClass}`}>{status}</span>;
  };

  const getTransactionDisplay = (tx: Transaction) => {
    switch (tx.type) {
      case 'bet':
        if (tx.metadata?.gameType === 'coinflip') {
          const isWin = tx.metadata?.result === 'win';
          const multiplier = tx.metadata?.multiplier || (isWin ? '1.9' : '0');
          return (
            <span>
              Coinflip{' '}
              <span className={isWin ? 'win-multiplier' : 'loss-multiplier'}>
                {isWin ? `${multiplier}x` : '0x'}
              </span>
            </span>
          );
        }
        return 'Bet';
      case 'payout':
        if (tx.metadata?.gameType === 'coinflip') {
          const multiplier = tx.metadata?.multiplier || '1.9';
          return (
            <span>
              Coinflip{' '}
              <span className="win-multiplier">{multiplier}x</span>
            </span>
          );
        }
        return 'Payout';
      case 'voucher_redemption':
        return 'Voucher Redemption';
      default:
        return tx.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  return (
    <>
      <SEO 
        title="Settings - SolBlox | Flip. Spin. Win."
        description="Manage your SolBlox account settings, view transaction history, and configure your affiliate program."
        url="https://solblox.com/settings"
      />
      <div className="settings-page">
        <h1 className="settings-title">Settings</h1>
        
        <div className="settings-layout">
          {/* Sidebar */}
          <div className="sidebar">
            <button
              className={`sidebar-tab ${activeView === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveView('profile')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              Profile
            </button>
            <button
              className={`sidebar-tab ${activeView === 'transactions' ? 'active' : ''}`}
              onClick={() => setActiveView('transactions')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23"/>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
              Transactions
            </button>
            <button
              className={`sidebar-tab ${activeView === 'affiliates' ? 'active' : ''}`}
              onClick={() => setActiveView('affiliates')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              Affiliates
            </button>
          </div>

          {/* Content Area */}
          <div className="settings-content">
            {activeView === 'profile' && (
              <div className="content-card">
                <h2>Profile Overview</h2>
                <div className="profile-container">
                  <div className="profile-image-section">
                    <img
                      src={getImageSrc()}
                      alt="Profile"
                      className="profile-image"
                      onError={(e) => {
                        e.currentTarget.src = phantomImg;
                      }}
                    />
                    <div className="profile-actions">
                      <button
                        className="settings-button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading || isRemoving}
                      >
                        {isUploading ? 'Uploading...' : 'Upload Picture'}
                      </button>
                      {(profileStats?.user.profilePicture || user.profilePicture) && (
                        <button
                          className="settings-button delete"
                          onClick={handleRemovePicture}
                          disabled={isUploading || isRemoving}
                        >
                          {isRemoving ? 'Removing...' : 'Delete Picture'}
                        </button>
                      )}
                    </div>
                    <p className="image-hint">JPEG, PNG, or WebP • Max 10MB</p>
                  </div>

                  <div className="profile-details">
                    <div className="detail-row">
                      <span className="settings-detail-label">Username</span>
                      <span className="detail-value">{profileStats?.user.username || user.username}</span>
                    </div>
                    <div className="detail-row">
                      <span className="settings-detail-label">Member Since</span>
                      <span className="detail-value">{formatDate(profileStats?.user.createdAt || user.createdAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeView === 'transactions' && (
              <div className="content-card">
                <h2>Transaction History</h2>
                {transactions.length === 0 ? (
                  <div className="empty-message">
                    <p>No transactions yet</p>
                  </div>
                ) : (
                  <>
                    <div className="table-wrapper">
                      <table className="transactions-table">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Amount (SOL)</th>
                            <th>Amount (USD)</th>
                            <th>Status</th>
                            <th>Date</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.map((tx) => (
                            <tr key={tx.id}>
                              <td>
                                <div className="transaction-type">
                                  <span className="type-icon">{getTypeIcon(tx.type)}</span>
                                  <span>{getTransactionDisplay(tx)}</span>
                                </div>
                              </td>
                              <td>
                                {(() => {
                                  const amount = (Number(tx.amount) / 1_000_000_000).toFixed(4);
                                  if (tx.type === 'bet' && tx.metadata?.gameType === 'coinflip') {
                                    const isWin = tx.metadata?.result === 'win';
                                    return (
                                      <span className={isWin ? 'win-amount' : 'loss-amount'}>
                                        {isWin ? `+${amount}` : `-${amount}`} SOL
                                      </span>
                                    );
                                  }
                                  if (tx.type === 'payout') {
                                    return <span className="win-amount">+{amount} SOL</span>;
                                  }
                                  if (tx.type === 'deposit') {
                                    return <span className="win-amount">+{amount} SOL</span>;
                                  }
                                  if (tx.type === 'withdrawal') {
                                    return <span className="loss-amount">-{amount} SOL</span>;
                                  }
                                  return `${amount} SOL`;
                                })()}
                              </td>
                              <td>${((Number(tx.amount) / 1_000_000_000) * solPrice).toFixed(2)}</td>
                              <td>{getStatusBadge(tx.status)}</td>
                              <td>
                                {new Date(tx.createdAt).toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </td>
                              <td>
                                <button
                                  className="copy-transaction-btn"
                                  onClick={() => copyTransactionData(tx)}
                                  title={tx.metadata?.gameType === 'coinflip' ? 'Copy game data for verification' : 'Copy transaction details'}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {totalPages > 1 && (
                      <div className="pagination-controls">
                        <button
                          className="pagination-button"
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="15 18 9 12 15 6"/>
                          </svg>
                          Previous
                        </button>
                        <span className="pagination-text">
                          Page {currentPage} of {totalPages}
                        </span>
                        <button
                          className="pagination-button"
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                        >
                          Next
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 18 15 12 9 6"/>
                          </svg>
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeView === 'affiliates' && (
              <div className="content-card">
                <h2>Affiliate System</h2>
                
                {!affiliateInfo ? (
                  <div className="loading-section">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <>
                    {affiliateInfo?.hasClaimedCode ? (
                      <div className="info-section">
                        <div className="info-banner">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="16" x2="12" y2="12"/>
                            <line x1="12" y1="8" x2="12.01" y2="8"/>
                          </svg>
                          <p>You have claimed <strong>{affiliateInfo.referredBy}</strong>'s affiliate code. You cannot have your own code while using someone else's.</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        {affiliateInfo && (
                          <div className="stats-section">
                            <div className="affiliate-stats">
                              <div className="stat-item">
                                <div className="stat-label">My Code</div>
                                <div className="stat-value">{affiliateInfo.affiliateCode || 'Not Set'}</div>
                                <div className="stat-hover-action">
                                  {affiliateInfo.affiliateCode && (
                                    <button
                                      onClick={() => copyToClipboard(affiliateInfo.affiliateCode)}
                                      className="stat-action"
                                    >
                                      Copy Code
                                    </button>
                                  )}
                                </div>
                              </div>
                              <div className="stat-item">
                                <div className="stat-label">Total Referrals</div>
                                <div className="stat-value">{affiliateStats?.stats?.totalReferrals || 0}</div>
                              </div>
                              <div className="stat-item earnings">
                                <div className="stat-label">Total Earned</div>
                                <div className="stat-value">
                                  {((Number(affiliateStats?.stats?.totalEarnings || 0) / 1e9).toFixed(4))} SOL
                                </div>
                                <div className="stat-hover-action">
                                  <button 
                                    className="stat-action claim"
                                    onClick={handleClaimEarnings}
                                    disabled={isClaimingEarnings || (Number(affiliateStats?.stats?.totalEarnings || 0) / 1e9) < 0.067}
                                  >
                                    {isClaimingEarnings ? 'Claiming...' : 'Claim Earnings'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="form-section">
                          <h3>{affiliateInfo?.affiliateCode ? 'Update Your Code' : 'Set Your Affiliate Code'}</h3>
                          
                          <div className="input-section">
                            <label>Affiliate Code</label>
                            <div className="settings-input-group">
                              <input
                                type="text"
                                value={affiliateCode}
                                onChange={(e) => {
                                  const value = e.target.value.toLowerCase().replace(/[^a-z]/g, '');
                                  setAffiliateCode(value);
                                }}
                                placeholder="Enter your custom code"
                                maxLength={20}
                                className="text-field"
                              />
                              <button
                                onClick={handleSaveAffiliateCode}
                                disabled={isSavingCode || !affiliateCode.trim() || affiliateCode === affiliateInfo?.affiliateCode}
                                className="action-button"
                              >
                                {isSavingCode ? 'Saving...' : affiliateInfo?.affiliateCode ? 'Update' : 'Save'}
                              </button>
                            </div>
                            <p className="field-hint">3-20 characters • Letters only (a-z)</p>
                          </div>

                          {affiliateInfo?.affiliateUrl && (
                            <div className="input-section">
                              <label>Your Affiliate URL</label>
                              <div className="settings-input-group">
                                <input
                                  type="text"
                                  value={affiliateInfo.affiliateUrl}
                                  readOnly
                                  className="text-field readonly"
                                />
                                <button
                                  onClick={() => copyToClipboard(affiliateInfo.affiliateUrl)}
                                  className="action-button affiliate-copy-btn"
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                                  </svg>
                                  Copy
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                                                  
                        {affiliateInfo?.affiliateCode && (
                          <div className="referrals-section">
                            <h3>Referred Users</h3>
                            {affiliateStats?.referrals && affiliateStats.referrals.length > 0 ? (
                              <>
                                <div className="table-wrapper">
                                  <table className="referrals-table">
                                    <thead>
                                      <tr>
                                        <th>Username</th>
                                        <th>Joined Date</th>
                                        <th>Total Wagered</th>
                                        <th>Your Earnings</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {affiliateStats.referrals.map((ref: any) => (
                                        <tr key={ref.id}>
                                          <td>{ref.username}</td>
                                          <td>{new Date(ref.joinedAt).toLocaleDateString()}</td>
                                          <td>{(Number(ref.totalWagered) / 1e9).toFixed(4)} SOL</td>
                                          <td>{(Number(ref.earnings) / 1e9).toFixed(4)} SOL</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>

                                {affiliateStats?.pagination?.totalPages > 1 && (
                                  <div className="pagination-controls">
                                    <button
                                      className="pagination-button"
                                      onClick={() => setAffiliatePage(prev => Math.max(1, prev - 1))}
                                      disabled={!affiliateStats.pagination.hasPrevPage}
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="15 18 9 12 15 6"/>
                                      </svg>
                                      Previous
                                    </button>
                                    <span className="pagination-text">
                                      Page {affiliateStats.pagination.currentPage} of {affiliateStats.pagination.totalPages}
                                    </span>
                                    <button
                                      className="pagination-button"
                                      onClick={() => setAffiliatePage(prev => prev + 1)}
                                      disabled={!affiliateStats.pagination.hasNextPage}
                                    >
                                      Next
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="9 18 15 12 9 6"/>
                                      </svg>
                                    </button>
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="empty-message">
                                <p>No referrals yet. Share your affiliate link to start earning!</p>
                              </div>
                            )}
                          </div>
                        )}

                        {!affiliateInfo?.affiliateCode && (
                          <div className="form-section">
                            <h3>Use Someone's Code</h3>
                            <p className="section-description">Enter someone's affiliate code to support them. You can only claim one code, and it's permanent.</p>
                            <div className="input-section">
                              <div className="settings-input-group">
                                <input
                                  type="text"
                                  value={claimCode}
                                  onChange={(e) => setClaimCode(e.target.value.toLowerCase())}
                                  placeholder="Enter affiliate code"
                                  className="text-field"
                                />
                                <button
                                  onClick={handleClaimCode}
                                  disabled={isClaimingCode || !claimCode.trim()}
                                  className="action-button"
                                >
                                  {isClaimingCode ? 'Claiming...' : 'Claim Code'}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </div>
    </>
  );
};

export default SettingsPage;